/**
 * Crafting System
 *
 * Handles item crafting and currency usage.
 * Based on Turkish mythology themes.
 *
 * Currency definitions and application logic have been moved to CurrencySystem.ts.
 * This file retains legacy crafting methods and recipe definitions for backwards compatibility,
 * and re-exports the new currency system for convenience.
 */

import { EquipmentItem, ItemRarity, ItemMod } from '@shared/types';
import { rollSingleMod, collectModGroups, rollModsForItem, getModNamePrefix, getModNameSuffix } from '@shared/items/ModifierPool';

// Re-export new currency system
export type {
  CurrencyType,
  CurrencyItemDef,
  CurrencyRarity,
  CurrencyResult,
} from './CurrencySystem';
export {
  CURRENCY_DEFINITIONS,
  applyCurrency,
  canApplyCurrency,
  rollCurrencyDrop,
  getTotalCurrencyDropWeight,
} from './CurrencySystem';

// Legacy crafting currency type (kept for backwards compatibility)
export type CraftingCurrencyType =
  | 'demir_parcasi'    // Normal → Magic (Transmute) - now ruh_tozu
  | 'ruh_damlasi'      // Magic → Rare (Alchemy) - now tengri_ozu
  | 'kam_boyasi'       // Reroll mods (Chaos) - now erlik_tasi
  | 'bereket_tozu'     // Add prefix - now ruh_muhru
  | 'ugur_tozu'        // Add suffix - now ruh_muhru
  | 'temizleme_suyu'   // Remove random mod - now yikim_tozu
  | 'sabitleme_tasi'   // Lock prefixes/suffixes during craft (removed)
  | 'koruma_muhru'     // Prevent destruction on enhancement fail (removed)
  | 'guclu_cekic'      // Higher enhancement success rate (removed)
  | 'kutsal_yag';      // Upgrade rare → sacred (removed)

export interface CraftingCurrency {
  id: string;
  type: CraftingCurrencyType;
  name: string;
  description: string;
  rarity: ItemRarity;
  stackSize: number;
  maxStack: number;
  icon: string;
}

/** @deprecated Use CURRENCY_DEFINITIONS from CurrencySystem.ts instead */
export const CRAFTING_CURRENCIES: Record<CraftingCurrencyType, Omit<CraftingCurrency, 'id' | 'stackSize'>> = {
  demir_parcasi: {
    type: 'demir_parcasi',
    name: 'Demir Parcasi',
    description: 'Normal itemi Sihirli yapar',
    rarity: 'normal',
    maxStack: 99,
    icon: '🔩',
  },
  ruh_damlasi: {
    type: 'ruh_damlasi',
    name: 'Ruh Damlasi',
    description: 'Sihirli itemi Nadir yapar',
    rarity: 'magic',
    maxStack: 50,
    icon: '💧',
  },
  kam_boyasi: {
    type: 'kam_boyasi',
    name: 'Kam Boyasi',
    description: 'Nadir itemin modlarini yeniden atar',
    rarity: 'rare',
    maxStack: 30,
    icon: '🎨',
  },
  bereket_tozu: {
    type: 'bereket_tozu',
    name: 'Bereket Tozu',
    description: 'Iteme rastgele prefix ekler',
    rarity: 'magic',
    maxStack: 50,
    icon: '✨',
  },
  ugur_tozu: {
    type: 'ugur_tozu',
    name: 'Ugur Tozu',
    description: 'Iteme rastgele suffix ekler',
    rarity: 'magic',
    maxStack: 50,
    icon: '🌟',
  },
  temizleme_suyu: {
    type: 'temizleme_suyu',
    name: 'Temizleme Suyu',
    description: 'Rastgele bir modu kaldirir',
    rarity: 'rare',
    maxStack: 30,
    icon: '💦',
  },
  sabitleme_tasi: {
    type: 'sabitleme_tasi',
    name: 'Sabitleme Tasi',
    description: 'Prefix veya suffix\'leri korur',
    rarity: 'rare',
    maxStack: 20,
    icon: '🔒',
  },
  koruma_muhru: {
    type: 'koruma_muhru',
    name: 'Koruma Muhru',
    description: 'Guclendirme basarisizliginda kirilmayi engeller',
    rarity: 'rare',
    maxStack: 20,
    icon: '🛡️',
  },
  guclu_cekic: {
    type: 'guclu_cekic',
    name: 'Guclu Cekic',
    description: 'Guclendirme basari sansini artirir (+10%)',
    rarity: 'rare',
    maxStack: 30,
    icon: '🔨',
  },
  kutsal_yag: {
    type: 'kutsal_yag',
    name: 'Kutsal Yag',
    description: 'Nadir itemi Kutsal yapar',
    rarity: 'legendary',
    maxStack: 10,
    icon: '🏺',
  },
};

/** @deprecated Enhancement system removed. Kept for save compatibility. */
export interface EnhancementResult {
  success: boolean;
  newLevel: number;
  destroyed: boolean;
  downgraded: boolean;
  message: string;
}

/** @deprecated Enhancement system removed. */
export const ENHANCEMENT_RATES: Record<number, { success: number; destroy: number; downgrade: number }> = {
  0: { success: 100, destroy: 0, downgrade: 0 },
  1: { success: 95, destroy: 0, downgrade: 0 },
  2: { success: 90, destroy: 0, downgrade: 0 },
  3: { success: 85, destroy: 0, downgrade: 0 },
  4: { success: 80, destroy: 0, downgrade: 0 },
  5: { success: 70, destroy: 0, downgrade: 5 },
  6: { success: 60, destroy: 3, downgrade: 12 },
  7: { success: 50, destroy: 3, downgrade: 17 },
  8: { success: 40, destroy: 5, downgrade: 22 },
  9: { success: 30, destroy: 5, downgrade: 28 },
  10: { success: 25, destroy: 8, downgrade: 30 },
  11: { success: 20, destroy: 10, downgrade: 30 },
  12: { success: 15, destroy: 12, downgrade: 30 },
  13: { success: 10, destroy: 15, downgrade: 30 },
  14: { success: 7, destroy: 18, downgrade: 30 },
};

// Enhancement stat bonuses
export const ENHANCEMENT_BONUSES: Record<number, number> = {
  0: 1.0,
  1: 1.05,
  2: 1.10,
  3: 1.15,
  4: 1.20,
  5: 1.30,  // First milestone
  6: 1.35,
  7: 1.40,
  8: 1.45,
  9: 1.50,
  10: 1.65, // Second milestone
  11: 1.75,
  12: 1.85,
  13: 1.95,
  14: 2.10,
  15: 2.50, // Max milestone
};

export interface CraftingResult {
  success: boolean;
  item?: EquipmentItem;
  message: string;
}

// Crafting Recipes for the Craft tab
export interface CraftingRecipe {
  id: string;
  name: string;
  description?: string;
  category: 'weapon' | 'armor' | 'accessory' | 'consumable' | 'material' | 'upgrade';
  goldCost: number;
  materials: { itemId: string; name: string; amount: number }[];
  requiresInput?: boolean;
  outputItemId?: string;
}

export const CRAFTING_RECIPES: CraftingRecipe[] = [
  {
    id: 'upgrade_magic',
    name: 'Sihirli Dönüşüm',
    description: 'Normal itemi Sihirli yapar',
    category: 'upgrade',
    goldCost: 100,
    materials: [{ itemId: 'demir_parcasi', name: 'Demir Parçası', amount: 1 }],
    requiresInput: true,
  },
  {
    id: 'upgrade_rare',
    name: 'Nadir Dönüşüm',
    description: 'Sihirli itemi Nadir yapar',
    category: 'upgrade',
    goldCost: 500,
    materials: [{ itemId: 'ruh_damlasi', name: 'Ruh Damlası', amount: 1 }],
    requiresInput: true,
  },
  {
    id: 'reroll_mods',
    name: 'Mod Yenileme',
    description: 'Nadir item modlarını yeniden atar',
    category: 'upgrade',
    goldCost: 300,
    materials: [{ itemId: 'kam_boyasi', name: 'Kam Boyası', amount: 1 }],
    requiresInput: true,
  },
  {
    id: 'add_prefix',
    name: 'Prefix Ekleme',
    description: 'İteme rastgele prefix ekler',
    category: 'upgrade',
    goldCost: 200,
    materials: [{ itemId: 'bereket_tozu', name: 'Bereket Tozu', amount: 1 }],
    requiresInput: true,
  },
  {
    id: 'add_suffix',
    name: 'Suffix Ekleme',
    description: 'İteme rastgele suffix ekler',
    category: 'upgrade',
    goldCost: 200,
    materials: [{ itemId: 'ugur_tozu', name: 'Uğur Tozu', amount: 1 }],
    requiresInput: true,
  },
  {
    id: 'health_potion',
    name: 'Can İksiri',
    description: 'Temel can iksiri üretir',
    category: 'consumable',
    goldCost: 50,
    materials: [{ itemId: 'ot', name: 'Şifalı Ot', amount: 3 }],
  },
  {
    id: 'mana_potion',
    name: 'Mana İksiri',
    description: 'Temel mana iksiri üretir',
    category: 'consumable',
    goldCost: 50,
    materials: [{ itemId: 'kristal', name: 'Mana Kristali', amount: 2 }],
  },
];

export class CraftingSystem {
  /**
   * Apply Demir Parçası - Normal → Magic
   */
  static applyDemirParcasi(item: EquipmentItem): CraftingResult {
    if (item.rarity !== 'normal') {
      return { success: false, message: 'Sadece Normal itemlere uygulanabilir' };
    }

    // Upgrade to magic and add 1 prefix + 1 suffix using slot-aware pool
    item.rarity = 'magic';
    const { prefixes, suffixes } = rollModsForItem(item, 1, 1, item.ilvl);
    item.prefixes = prefixes;
    item.suffixes = suffixes;
    item.name = this.regenerateName(item);

    return { success: true, item, message: 'Item Sihirli oldu!' };
  }

  /**
   * Apply Ruh Damlası - Magic → Rare
   */
  static applyRuhDamlasi(item: EquipmentItem): CraftingResult {
    if (item.rarity !== 'magic') {
      return { success: false, message: 'Sadece Sihirli itemlere uygulanabilir' };
    }

    // Upgrade to rare and add more mods using slot-aware pool
    item.rarity = 'rare';
    const existingGroups = collectModGroups(item);

    // Add 1 more prefix (total 2)
    if (item.prefixes.length < 2) {
      const mod = rollSingleMod(item, 'prefix', existingGroups, item.ilvl);
      if (mod) item.prefixes.push(mod);
    }

    // Add 1 more suffix (total 2)
    if (item.suffixes.length < 2) {
      const mod = rollSingleMod(item, 'suffix', existingGroups, item.ilvl);
      if (mod) item.suffixes.push(mod);
    }

    item.name = this.regenerateName(item);

    return { success: true, item, message: 'Item Nadir oldu!' };
  }

  /**
   * Apply Kam Boyası - Reroll rare item mods
   */
  static applyKamBoyasi(item: EquipmentItem, lockPrefixes: boolean = false, lockSuffixes: boolean = false): CraftingResult {
    if (item.rarity !== 'rare' && item.rarity !== 'set') {
      return { success: false, message: 'Sadece Nadir veya Set itemlere uygulanabilir' };
    }

    // Reroll mods (respect locks) using slot-aware pool
    const prefixCount = lockPrefixes ? 0 : (1 + Math.floor(Math.random() * 2));
    const suffixCount = lockSuffixes ? 0 : (1 + Math.floor(Math.random() * 2));

    if (!lockPrefixes) {
      item.prefixes = [];
    }
    if (!lockSuffixes) {
      item.suffixes = [];
    }

    const { prefixes, suffixes } = rollModsForItem(item, prefixCount, suffixCount, item.ilvl);
    if (!lockPrefixes) item.prefixes = prefixes;
    if (!lockSuffixes) item.suffixes = suffixes;

    item.name = this.regenerateName(item);

    return { success: true, item, message: 'Modlar yeniden atıldı!' };
  }

  /**
   * Apply Bereket Tozu - Add prefix
   */
  static applyBereketTozu(item: EquipmentItem): CraftingResult {
    const maxPrefixes = this.getMaxMods(item.rarity);
    if (item.prefixes.length >= maxPrefixes) {
      return { success: false, message: 'Maksimum prefix sayısına ulaşıldı' };
    }

    const existingGroups = collectModGroups(item);
    const mod = rollSingleMod(item, 'prefix', existingGroups, item.ilvl);
    if (mod) {
      item.prefixes.push(mod);
    }
    item.name = this.regenerateName(item);

    return { success: true, item, message: 'Yeni prefix eklendi!' };
  }

  /**
   * Apply Uğur Tozu - Add suffix
   */
  static applyUgurTozu(item: EquipmentItem): CraftingResult {
    const maxSuffixes = this.getMaxMods(item.rarity);
    if (item.suffixes.length >= maxSuffixes) {
      return { success: false, message: 'Maksimum suffix sayısına ulaşıldı' };
    }

    const existingGroups = collectModGroups(item);
    const mod = rollSingleMod(item, 'suffix', existingGroups, item.ilvl);
    if (mod) {
      item.suffixes.push(mod);
    }
    item.name = this.regenerateName(item);

    return { success: true, item, message: 'Yeni suffix eklendi!' };
  }

  /**
   * Apply Temizleme Suyu - Remove random mod
   */
  static applyTemizlemeSuyu(item: EquipmentItem): CraftingResult {
    const totalMods = item.prefixes.length + item.suffixes.length;
    if (totalMods === 0) {
      return { success: false, message: 'Kaldırılacak mod yok' };
    }

    const removePrefix = Math.random() < (item.prefixes.length / totalMods);
    if (removePrefix && item.prefixes.length > 0) {
      const idx = Math.floor(Math.random() * item.prefixes.length);
      item.prefixes.splice(idx, 1);
    } else if (item.suffixes.length > 0) {
      const idx = Math.floor(Math.random() * item.suffixes.length);
      item.suffixes.splice(idx, 1);
    }

    item.name = this.regenerateName(item);

    return { success: true, item, message: 'Rastgele bir mod kaldırıldı!' };
  }

  /**
   * Apply Kutsal Yağ - Rare → Sacred
   */
  static applyKutsalYag(item: EquipmentItem): CraftingResult {
    if (item.rarity !== 'rare') {
      return { success: false, message: 'Sadece Nadir itemlere uygulanabilir' };
    }

    item.rarity = 'legendary';

    // Add 1 more prefix if possible (up to 3) using slot-aware pool
    if (item.prefixes.length < 3) {
      const existingGroups = collectModGroups(item);
      const mod = rollSingleMod(item, 'prefix', existingGroups, item.ilvl);
      if (mod) item.prefixes.push(mod);
    }

    item.name = this.regenerateName(item);

    return { success: true, item, message: 'Item Kutsal oldu!' };
  }

  /**
   * Enhance item (+basma)
   */
  static enhance(
    item: EquipmentItem,
    useProtection: boolean = false,
    useHammer: boolean = false
  ): EnhancementResult {
    const currentLevel = item.enhancement || 0;

    if (currentLevel >= 15) {
      return {
        success: false,
        newLevel: currentLevel,
        destroyed: false,
        downgraded: false,
        message: 'Maksimum güçlendirme seviyesine ulaşıldı',
      };
    }

    const rates = ENHANCEMENT_RATES[currentLevel];
    let successRate = rates.success;
    let destroyRate = rates.destroy;
    const downgradeRate = rates.downgrade;

    // Apply hammer bonus
    if (useHammer) {
      successRate += 10;
      destroyRate = Math.max(0, destroyRate - 5);
    }

    const roll = Math.random() * 100;

    if (roll < successRate) {
      // Success!
      item.enhancement = currentLevel + 1;
      return {
        success: true,
        newLevel: item.enhancement,
        destroyed: false,
        downgraded: false,
        message: `Güçlendirme başarılı! +${item.enhancement}`,
      };
    } else if (roll < successRate + destroyRate && !useProtection) {
      // Destroyed!
      return {
        success: false,
        newLevel: 0,
        destroyed: true,
        downgraded: false,
        message: 'Item kırıldı!',
      };
    } else if (roll < successRate + destroyRate + downgradeRate) {
      // Downgraded
      const newLevel = Math.max(0, currentLevel - 1);
      item.enhancement = newLevel;
      return {
        success: false,
        newLevel,
        destroyed: false,
        downgraded: true,
        message: `Güçlendirme düştü! +${newLevel}`,
      };
    } else {
      // Just failed, no change
      return {
        success: false,
        newLevel: currentLevel,
        destroyed: false,
        downgraded: false,
        message: 'Güçlendirme başarısız',
      };
    }
  }

  /**
   * Get item stats with enhancement bonus applied
   */
  static getEnhancedStats(item: EquipmentItem): ItemMod[] {
    const bonus = ENHANCEMENT_BONUSES[item.enhancement || 0];
    const allMods = [...item.implicit, ...item.prefixes, ...item.suffixes];

    return allMods.map(mod => ({
      ...mod,
      value: Math.floor(mod.value * bonus),
    }));
  }

  /**
   * Get enhancement display color
   */
  static getEnhancementColor(level: number): string {
    if (level >= 15) return '#ff00ff'; // Pink/Magenta
    if (level >= 10) return '#ff8800'; // Orange
    if (level >= 5) return '#ffff00';  // Yellow
    if (level > 0) return '#00ff00';   // Green
    return '#ffffff';                   // White
  }

  /**
   * Get enhancement display text
   */
  static getEnhancementText(level: number): string {
    if (level <= 0) return '';
    return `+${level}`;
  }

  // Helper methods
  private static getMaxMods(rarity: ItemRarity): number {
    switch (rarity) {
      case 'normal': return 0;
      case 'magic': return 1;
      case 'rare': return 2;
      case 'set': return 3;
      case 'legendary': return 3;
      default: return 0;
    }
  }

  private static regenerateName(item: EquipmentItem): string {
    // Pick best prefix and suffix (highest value mod)
    const bestPrefix = item.prefixes.length > 0
      ? item.prefixes.reduce((best, m) => m.value > best.value ? m : best)
      : null;
    const bestSuffix = item.suffixes.length > 0
      ? item.suffixes.reduce((best, m) => m.value > best.value ? m : best)
      : null;

    const prefix = bestPrefix ? getModNamePrefix(bestPrefix.type, bestPrefix.calcType) : '';
    const suffix = bestSuffix ? getModNameSuffix(bestSuffix.type, bestSuffix.calcType) : '';

    // Use stored baseName for reliable regeneration (survives multiple crafting operations)
    const baseName = item.baseName || item.name;

    return `${prefix ? prefix + ' ' : ''}${baseName}${suffix ? ' ' + suffix : ''}`;
  }
}

// Helper function to create currency
let currencyIdCounter = 0;
export function createCurrency(type: CraftingCurrencyType, amount: number = 1): CraftingCurrency {
  const base = CRAFTING_CURRENCIES[type];
  return {
    ...base,
    id: `currency_${Date.now()}_${currencyIdCounter++}`,
    stackSize: Math.min(amount, base.maxStack),
  };
}
